import { reaction } from "mobx"
import { useEffect } from "react"

import { useForceUpdate } from "./useForceUpdate"

/**
 * Хук, который подписывается на изменение состояния хранилища, созданного с помощью mobx, и возвращает значение этого состояния
 * @param store хранилище состояний
 * @param stateKey имя состояния
 */
export function useStoreStateValue<S, K extends keyof S>(store: S, stateKey: K) {
	const forceUpdate = useForceUpdate()

	useEffect(() => {
		return reaction(() => store[stateKey], forceUpdate)
	}, [])

	return store[stateKey]
}

/**
 * Хук, который возвращает функцию, для изменения состояния хранилища, созданного с помощью mobx
 * @param store хранилище состояний
 * @param stateKey имя состояния
 */
export function useSetStoreState<S, K extends keyof S>(store: S, stateKey: K) {
	return (value: S[K] | ((curValue : S[K]) => S[K])) => store[stateKey] = value instanceof Function
		? value(store[stateKey])
		: value
}

/**
 * Хук, который объединяет {@link useStoreStateValue} и {@link useSetStoreState}.
 * Он возвращает, подобно `useState`, массив со значение состояния и сеттером
 * @param store
 * @param stateKey
 */
export function useStoreState<S, K extends keyof S>(store: S, stateKey: K) {
	const value = useStoreStateValue(store, stateKey)
	const set = useSetStoreState<S, K>(store, stateKey)
	return [value, set] as const
}